/* ============================================================
   Every scripted movement on the page, in one place.

   CSS keeps what CSS is good at — hover and focus state transitions.
   GSAP owns everything with a timeline or a scroll position behind it,
   so there is one authored sequence rather than eight competing ones,
   and one place to turn all of it off.

   GSAP owns the start states too. Declaring them in CSS as percentage
   transforms looks equivalent and is not: the computed matrix resolves
   the percentage to pixels, GSAP reads those pixels as `y`, and its own
   `yPercent` tween then rides on top of an offset it never zeroes.
   ============================================================ */

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { RefObject } from 'react';
import { prefersReducedMotion } from '../lib/motion.ts';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function useSiteMotion(scope: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      const root = scope.current;
      if (!root) return;
      const q = gsap.utils.selector(root);

      // Reduced motion is not "no design" — it is the same composition,
      // arrived at instantly.
      if (prefersReducedMotion()) {
        gsap.set(q('.reveal'), { autoAlpha: 1, y: 0 });
        return;
      }

      // ── The hero is intentionally quiet: the isometric field settles once, then
      //    the copy follows in a short editorial sequence.
      gsap.fromTo(
        q('.hero__visual'),
        { autoAlpha: 0, scale: 1.025 },
        { autoAlpha: 1, scale: 1, duration: 1.35, ease: 'power3.out' },
      );

      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .from(q('.hero__title .w'), { yPercent: 104, duration: 0.95, stagger: 0.07 }, 0.2)
        .from(q('.hero__lede'), { autoAlpha: 0, y: 14, duration: 0.72 }, '-=0.52')
        .from(q('.hero__actions > *'), { autoAlpha: 0, y: 10, duration: 0.62, stagger: 0.06 }, '-=0.46')
        .from(q('.hero__strip > *'), { autoAlpha: 0, duration: 0.55, stagger: 0.05 }, '-=0.32');

      gsap.to(q('.hero__visual'), {
        yPercent: 3,
        ease: 'none',
        scrollTrigger: {
          trigger: q('.hero')[0],
          start: 'top top',
          end: 'bottom top',
          scrub: 0.8,
        },
      });

      // ── Reveals, batched so a grid cascades instead of snapping in as one block.
      ScrollTrigger.batch(q('.reveal'), {
        start: 'top 88%',
        once: true,
        onEnter: (els) =>
          gsap.to(els, {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            ease: 'expo.out',
            stagger: 0.07,
            overwrite: true,
          }),
      });

      // ── Display type drifts against the scroll, in alternating directions.
      //    Kept small on purpose: at 4.5vw each heading travelled ~130px on a
      //    desktop and ran out past the edge of its own panel. The movement
      //    should be noticed on the second read, not the first.
      for (const el of q('.drift')) {
        const dir = el.classList.contains('drift--rev') ? -1 : 1;
        gsap.fromTo(
          el,
          { x: `${-1.1 * dir}vw` },
          {
            x: `${1.1 * dir}vw`,
            ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
          },
        );
      }
    },
    { scope },
  );
}
